<script>
  import { createIntersectionObserver, waitForElement } from '../components/observerHelper';

  export default {
    name: 'Quicklinks',
    props: {
      /**
       * [
       *   {
       *     displayTitle?: String,
       *     title: String,
       *     icon?: Path to image,
       *   }
       * ]
       */
      sections: {
        type: Array,
        required: true,
      },
      isMobile: {
        type: Boolean,
      },
      isDesktop: {
        type: Boolean,
      },
      hideIndex: {
        type: Boolean,
        required: false,
      },
    },
    data: () => ({
      isActive: false,
      activeSection: {},
      screenWidth: 1441,
      styles: {
        wrapper: {
          opacity: 0,
          top: 0,
          left: 0,
          height: 'auto',
        },
        listContainer: {
          height: 'auto',
        },
      },
      intersectionObserver: null,
      pageHeaderHeight: 0,
      offsetY: 40,
      pageFooter: {
        element: null,
        isInViewport: false,
      },
    }),
    computed: {
      hasIcons: ({ sections }) => sections.some((section) => !!section.icon),
      isMobileStyle: ({ isDesktop, isMobile, screenWidth }) => (!isDesktop && screenWidth < 1441) || isMobile,
      wrapperStyle: ({ styles }) => ({
        opacity: styles.wrapper.opacity,
        top: styles.wrapper.top + 'px',
        left: styles.wrapper.left + 'px',
        // height: styles.wrapper.height ? styles.wrapper.height + 'px' : 'auto',
      }),
      listContainerStyle: ({ styles }) => ({
        maxHeight: styles.listContainer.height ? styles.listContainer.height + 'px' : 'auto',
      }),
    },
    methods: {
      sectionToHash: (section) => section.title.toLowerCase().replace(/ /g, '-'),
      handleClick(section) {
        this.isActive = !this.isActive;
        if (section) {
          this.$emit('click', this.sectionToHash(section));
          history.replaceState(null, null, '#' + this.sectionToHash(section));
        }
        //FIXME: fire calculateHeight after scroll end
        setTimeout(() => this.calculateHeight(), 1000);
      },
      handleHashChange() {
        this.activeSection =
          this.sections.find((section) => this.sectionToHash(section) === location.hash.substring(1)) ||
          this.sections[0];
        document.getElementById(`a_${this.sectionToHash(this.activeSection)}`).scrollIntoView({ behavior: 'smooth' });
      },
      getOffsetLeft(selector = '.content') {
        const targetEl = document.querySelector(selector),
          quicklinksEl = this.$refs.quicklinks;
        if (!targetEl || !quicklinksEl) return;

        let targetLeft = targetEl.getBoundingClientRect().left,
          quicklinksOffset = quicklinksEl.getBoundingClientRect().width;

        if (window.innerWidth < 1441) {
          quicklinksOffset = 0;
        } else {
          quicklinksOffset += 40;
        }
        return targetLeft - quicklinksOffset;
      },
      calculateHeight() {
        setTimeout(() => {
          let availableHeight = window.innerHeight - this.pageHeaderHeight - this.offsetY * 2;
          if (this.pageFooter.isInViewport && window.innerWidth > 1440) {
            const rect = this.pageFooter.element.getBoundingClientRect();
            availableHeight -= window.innerHeight - rect.top;
          }
          // this.styles.wrapper.height = availableHeight;
          this.styles.listContainer.height = availableHeight - 96;
        }, 200);
      },
      updatePosition() {
        setTimeout(() => {
          this.screenWidth = window.innerWidth;
          this.styles.wrapper.left = this.getOffsetLeft();
          if (this.screenWidth <= 1441) {
            this.offsetY = 16;
          } else {
            this.offsetY = 40;
          }
          this.styles.wrapper.top = this.pageHeaderHeight + this.offsetY;
        }, 200);
      },
      createOrRemoveListeners(isRemove = false) {
        if (isRemove) {
          window.removeEventListener('history-replace-state', this.handleHashChange);
          window.removeEventListener('resize', this.updatePosition);
          window.removeEventListener('wheel', this.calculateHeight);
        } else {
          window.addEventListener('history-replace-state', this.handleHashChange);
          window.addEventListener('resize', this.updatePosition);
          window.addEventListener('wheel', this.calculateHeight);
          // window.addEventListener('touchmove', this.calculateHeight);
        }
      },
    },
    created() {
      const replaceState = history.replaceState;
      history.replaceState = function (state, title, url) {
        replaceState.call(this, state, title, url);
        window.dispatchEvent(new CustomEvent('history-replace-state', { state }));
      };
    },
    async mounted() {
      this.handleHashChange();
      this.createOrRemoveListeners();

      this.pageFooter.element = await waitForElement('footer');
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      this.intersectionObserver = createIntersectionObserver(
        ([entry]) => (this.pageFooter.isInViewport = entry.isIntersecting),
        'footer',
      );

      const pageHeader = await waitForElement('header');
      this.pageHeaderHeight = pageHeader.getBoundingClientRect().height;

      this.calculateHeight();
      this.updatePosition();
      setTimeout(() => (this.styles.wrapper.opacity = 1), 500);
    },
    beforeDestroy() {
      this.createOrRemoveListeners(true);
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    },
  };
</script>

<template>
  <div
    ref="quicklinks"
    class="quicklinks pa-6"
    :class="{
      active: this.screenWidth < 1441 && isActive,
      mobile: isMobileStyle,
    }"
    :style="wrapperStyle"
    @click.self="handleClick()"
  >
    <span class="white--text d-flex no-pointer-events">
      Quicklinks
      <span v-if="isMobileStyle" class="ml-auto chevron"><v-icon color="white">mdi-chevron-down</v-icon></span>
    </span>
    <div class="quicklinks-container mt-6 pr-3" :class="{ icons: hasIcons }" :style="listContainerStyle">
      <div
        v-for="(section, i) in sections"
        :key="section.title + i"
        class="quicklink-item d-flex align-center mb-1 cursor-pointer"
        :class="{ active: section === activeSection }"
        :id="'a_' + sectionToHash(section)"
        @click="handleClick(section)"
      >
        <span
          v-if="section.icon"
          class="d-flex justify-center align-center icon"
          :style="{ '--icon': `url(${section.icon})` }"
        />
        <span :class="{ 'ml-5': !section.icon }">
          {{ !hideIndex ? `${++i}. ` : '' }}{{ section.displayTitle || section.title }}
        </span>
      </div>
    </div>
  </div>
</template>
<style>
  /*
    TODO: This is dirty, test if it affects pages outside where this component is rendered
   */
  .v-main {
    transform-style: unset !important;
  }
</style>
<style scoped lang="scss">
  .quicklinks {
    position: fixed;
    top: 122px;
    transition:
      left 200ms,
      height 200ms,
      background-color 300ms,
      opacity 500ms;
    @media #{$viewport-desktop-large-down} {
      top: 98px;
    }
    @media (max-width: 959px) {
      display: none;
      left: 20px;
    }

    z-index: 2;
    background-color: #24073d;
    width: 100%;
    height: fit-content;
    max-width: 448px;
    border-radius: 10px;

    & > span {
      opacity: 0.6;
      font-size: 14px;
    }

    &-container {
      // max-height: 35vh;
      overflow-y: auto;
      will-change: height, margin-top;
      transition:
        height 200ms ease-in-out,
        margin-top 300ms ease;

      div.quicklink-item {
        height: 40px;
        color: #fff;

        span {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          user-select: none;
        }
      }

      &.icons {
        div.quicklink-item {
          height: 60px;

          span.icon {
            width: 60px;
            height: 44px;
            background-color: #fff;
            mask: var(--icon) no-repeat center;
            mask-size: 24px 20px;
          }
        }
      }

      div.quicklink-item {
        display: block;
        border-radius: 10px;
        overflow: hidden;

        &.active,
        &:active {
          background-color: rgba(#f8bf1e, 0.05);
        }

        @media (hover: hover) {
          &:hover {
            background-color: rgba(#f8bf1e, 0.05);
          }
        }

        &.active {
          color: #f8bf1e;
          font-weight: 600;

          span.icon {
            background-color: #f8bf1e;
          }

          &:before {
            display: block;
            content: '';
            height: 100%;
            width: 4px;
            background-color: #f8bf1e;
          }
        }
      }

      &::-webkit-scrollbar-thumb {
        background: #350f53 !important;
      }

      &::-webkit-scrollbar-track {
        background: #1c0630 !important;
      }
    }

    &.mobile {
      width: calc(100% - 40px);
      background-color: #fff;
      @media #{$viewport-tablet-down} {
        height: unset !important;
        .quicklinks-container {
          max-height: 55vh;
        }
      }

      &:not(.active) {
        height: unset !important;

        & > span {
          color: rgba(#000, 0.8) !important;

          i {
            color: #000 !important;
          }
        }

        .quicklinks-container {
          height: 0 !important;
          margin-top: 0 !important;
        }
      }

      &.active {
        background-color: #24073d;

        .quicklinks-container {
          //height: 35vh;
          //@media #{$viewport-desktop-down} {
          //  height: 53vh;
          //}
          //max-height: 53vh;
          margin-top: 24px !important;
        }
      }
    }
  }
</style>
